import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { filtrarPreguntas, PASOS, PreguntaGuia, RolGuia, ROLES, rolDesdeUrl, rolPorPermisos, urlVideoDrive } from 'src/app/utils/liquidacion-guia';
import { PREGUNTAS_GUIA } from './guia-preguntas';

/**
 * GUÍA DE LIQUIDACIÓN (GUI-01..13): el proceso de punta a punta, un video por rol y las preguntas frecuentes.
 * Vive como pestaña de Liquidación y también como ruta propia (`console/liquidacion/guia`) para quien verifica cuentas.
 * El rol sale, en este orden: @Input, `?rol=` de la URL, permisos del usuario (GUI-02).
 */
@Component({
  selector: 'app-liquidacion-guia',
  templateUrl: './liquidacion-guia.component.html'
})
export class LiquidacionGuiaComponent implements OnInit {
  @Input() rol: RolGuia | null = null;

  readonly roles = ROLES;
  readonly pasos = PASOS;
  readonly preguntas: PreguntaGuia[] = PREGUNTAS_GUIA;

  /** Ruta propia: lleva su propio contenedor de página y título. */
  pagina = false;
  texto = '';
  todas = false;

  /** GUI-14: reproductor de Drive del rol elegido; null → «en preparación». */
  videoUrl: SafeResourceUrl | null = null;

  // Haz tu pregunta
  textoPregunta = '';
  enviando = false;
  exito = '';
  errorPregunta = '';

  // Preguntas por responder (permiso liquidacion.guia_responder)
  pendientes: any[] = [];
  marcando: string | null = null;
  errorPendientes = '';

  constructor(private liq: LiquidacionService, private _auth: AuthService, private ruta: ActivatedRoute, private sanitizer: DomSanitizer) { }

  /** GUI-08: responder preguntas es un permiso (liquidacion.guia_responder), no un perfil. */
  get esOwner(): boolean { return this._auth.hasPermission('liquidacion.guia_responder'); }
  get nombreRol(): string { return (ROLES.find((r) => r.id === this.rol) || ROLES[3]).nombre; }
  get visibles(): PreguntaGuia[] { return filtrarPreguntas(this.preguntas, { texto: this.texto, rol: this.rol as RolGuia, todas: this.todas }); }
  /** Caben 5 caracteres como mínimo (GUI-07); el núcleo vuelve a validarlo. */
  get puedeEnviar(): boolean { return !this.enviando && !(this.textoPregunta.trim().length < 5); }

  ngOnInit(): void {
    this.pagina = this.ruta.snapshot.routeConfig?.path === 'console/liquidacion/guia';
    this.rol = rolDesdeUrl(this.rol) || rolDesdeUrl(this.ruta.snapshot.queryParamMap.get('rol')) || rolPorPermisos((p) => this._auth.hasPermission(p));
    this.cargarVideo();
    if (this.esOwner) this.cargarPendientes();
  }

  /** GUI-03: cambia el video, los pasos marcados y el filtro sin recargar. */
  elegirRol(r: RolGuia): void {
    if (r === this.rol) return;
    this.rol = r;
    this.cargarVideo();
  }

  participa = (roles: RolGuia[]): boolean => roles.includes(this.rol as RolGuia);

  /** GUI-14: la URL sale solo de `urlVideoDrive` (archivo fijo por rol), por eso se puede marcar como segura. */
  cargarVideo(): void {
    const url = urlVideoDrive(this.rol);
    this.videoUrl = url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  }

  async preguntar(): Promise<void> {
    if (!this.puedeEnviar) return;
    this.enviando = true; this.exito = ''; this.errorPregunta = '';
    try {
      await this.liq.guiaPreguntar(this.textoPregunta.trim(), this.rol as RolGuia);
      this.exito = 'Pregunta registrada. La respondemos y la sumamos a la lista.';
      this.textoPregunta = '';
      if (this.esOwner) this.cargarPendientes();
    } catch (e: any) {
      this.errorPregunta = e?.status === 429
        ? 'Ya tienes 10 preguntas pendientes por responder. Cuando las respondamos podrás enviar más.'
        : 'No pudimos registrar tu pregunta. Inténtalo de nuevo en unos minutos.';
    } finally {
      this.enviando = false;
    }
  }

  async cargarPendientes(): Promise<void> {
    this.errorPendientes = '';
    try {
      const r = await this.liq.guiaPendientes();
      this.pendientes = Array.isArray(r) ? r : (r && (r.preguntas || r.data)) || [];
    } catch (e) {
      this.errorPendientes = 'No pudimos traer las preguntas por responder.';
    }
  }

  async marcarRespondida(p: any): Promise<void> {
    const id = p._id || p.id;
    this.marcando = id;
    try {
      await this.liq.guiaMarcarRespondida(id);
      this.pendientes = this.pendientes.filter((x) => (x._id || x.id) !== id);
    } catch (e) {
      this.errorPendientes = 'No pudimos marcarla como respondida. Inténtalo de nuevo.';
    } finally {
      this.marcando = null;
    }
  }

  porId = (_: number, x: any): string => x._id || x.id;
}
